import { makeTheme } from 'dripsy';
import { Platform } from 'react-native';
const FONT_SIZES = {
    $0: 12,
    $1: 14,
    $2: 16,
    $3: 20,
    $4: 24,
    $5: 32,
    $6: 40,
    $7: 48,
    $8: 56,
    $9: 64,
};
const SPACE_AND_SIZES = {
    $0: 0,
    $1: 4,
    $2: 8,
    $3: 12,
    $4: 16,
    $5: 20,
    $6: 24,
    $7: 32,
    $8: 40,
    $9: 48,
    $10: 56,
    $11: 64,
    $12: 72,
    $13: 80,
    $14: 96,
    $15: 112,
    $16: 128,
    $17: 144,
};
export const DoppioBreakpointSmall = '480px';
export const DoppioBreakpointMedium = '768px';
export const DoppioBreakpointLarge = '1024px';
export const DoppioBreakpoints = [
    DoppioBreakpointSmall,
    DoppioBreakpointMedium,
    DoppioBreakpointLarge,
];
const theme = makeTheme({
    breakpoints: DoppioBreakpoints,
    colors: {
        text: '#000',
        background: '#fff',
        backgroundMedium: '#000',
        backgroundWeak: '#000',
        backgroundWeakest: '#000',
        border: '#000',
        primaryBackground: '#fff',
        primaryBackgroundHover: '#fff',
        primaryBackgroundActive: '#fff',
        primaryBackgroundWeak: '#fff',
        secondaryText: '#fff',
        secondaryBackground: '#000',
        secondaryBackgroundHover: '#000',
        secondaryBackgroundActive: '#000',
        secondaryBackgroundDisabled: '#000',
        secondaryBorder: '#000',
        secondaryBorderDisabled: '#000',
        secondaryIcon: '#fff',
        secondaryIconDisabled: '#000',
        tertiaryText: '#000',
        tertiaryBackground: '#fff',
        tertiaryBorder: '#000',
        warningBackground: '#fff',
        warningBackgroundHover: '#fff',
        warningBackgroundActive: '#fff',
        warningBackgroundWeak: '#fff',
        warningIcon: '#000',
        successBackground: '#fff',
        successBackgroundHover: '#fff',
        successBackgroundActive: '#fff',
        successBackgroundWeak: '#fff',
        successIcon: '#000',
        infoBackground: '#fff',
        infoBackgroundHover: '#fff',
        infoBackgroundActive: '#fff',
        infoBackgroundWeak: '#fff',
        infoBorder: '#000',
        infoIcon: '#000',
        errorText: '#fff',
        errorBackground: '#000',
        errorBackgroundHover: '#000',
        errorBackgroundActive: '#000',
        errorBackgroundWeak: '#000',
        errorBorder: '#000',
        errorIcon: '#000',
    },
    space: { ...SPACE_AND_SIZES },
    sizes: {
        ...SPACE_AND_SIZES,
        sliderThumb: 16,
        sliderTrackThickness: 1,
        avatarDefault: 32,
        avatarSmall: 24,
        avatarBig: 40,
    },
    fontSizes: { ...FONT_SIZES },
    lineHeights: { ...FONT_SIZES },
    radii: {
        none: 0,
        subtle: 4,
        default: 8,
        full: 9999,
    },
    fonts: {
        root: 'System',
        italic: 'System',
        monospace: Platform.select({
            web: `'SF Mono', SFMono-Regular, ui-monospace, 'Roboto Mono', 'DejaVu Sans Mono', Menlo, Consolas, monospace`,
            ios: 'Menlo',
            android: 'monospace',
        }),
    },
    fontWeights: {
        normal: '500',
        medium: '600',
        bold: '700',
    },
    zIndices: {
        $0: 0,
        $1: 9,
        $2: 99,
        $3: 999,
        $4: 9999,
    },
    text: {
        displayLarge: {
            fontSize: '$8',
            lineHeight: '$9',
            fontWeight: 'bold',
        },
        displayMedium: {
            fontSize: '$7',
            lineHeight: '$8',
            fontWeight: 'bold',
        },
        displaySmall: {
            fontSize: '$6',
            lineHeight: '$7',
            fontWeight: 'bold',
        },
        headingLarge: {
            fontSize: '$5',
            lineHeight: '$6',
            fontWeight: 'bold',
        },
        headingMedium: {
            fontSize: '$4',
            lineHeight: '$5',
            fontWeight: 'bold',
        },
        headingSmall: {
            fontSize: '$3',
            lineHeight: '$4',
            fontWeight: 'bold',
        },
        paragraphLarge: {
            fontSize: '$2',
            lineHeight: '$4',
            fontWeight: 'normal',
        },
        paragraphMedium: {
            fontSize: '$1',
            lineHeight: '$3',
            fontWeight: 'normal',
        },
        paragraphSmall: {
            fontSize: '$0',
            lineHeight: '$3',
            fontWeight: 'normal',
        },
        labelLarge: {
            fontSize: '$2',
            lineHeight: '$3',
            fontWeight: 'medium',
        },
        labelMedium: {
            fontSize: '$1',
            lineHeight: '$3',
            fontWeight: 'medium',
        },
        labelSmall: {
            fontSize: '$0',
            lineHeight: '$2',
            fontWeight: 'medium',
        },
        monoLarge: {
            fontSize: '$2',
            lineHeight: '$3',
            fontFamily: 'monospace',
        },
        monoSmall: {
            fontSize: '$0',
            lineHeight: '$2',
            fontFamily: 'monospace',
        },
        a: {
            fontSize: '$2',
            lineHeight: '$4',
            fontWeight: 'bold',
            color: 'infoBackground',
        },
        p: {
            fontSize: '$1',
            lineHeight: '$3',
            fontWeight: 'normal',
        },
    },
    shadows: {
        pop: {
            shadowColor: 'secondaryBackground',
            shadowOffset: {
                width: 0,
                height: 4,
            },
            shadowOpacity: 0.12,
            shadowRadius: 24,
            elevation: 4,
        },
    },
    buttons: {
        primary: {
            fontWeight: 'bold',
            borderRadius: 'none',
            backgroundColor: 'primaryBackground',
            color: 'text',
            outlineColor: 'primaryBackground',
            borderColor: 'transparent',
            $$hover: {
                backgroundColor: 'primaryBackgroundHover',
            },
            $$active: {
                backgroundColor: 'primaryBackgroundActive',
            },
            $$disabled: {
                color: 'secondaryText',
                backgroundColor: 'secondaryBackgroundDisabled',
            },
        },
        secondary: {
            fontWeight: 'bold',
            borderRadius: 'none',
            backgroundColor: 'secondaryBackground',
            color: 'tertiaryText',
            outlineColor: 'secondaryBackground',
            borderColor: 'transparent',
            $$hover: {
                backgroundColor: 'secondaryBackgroundHover',
            },
            $$active: {
                color: 'tertiaryText',
                backgroundColor: 'secondaryBackgroundActive',
            },
            $$disabled: {
                color: 'secondaryText',
                backgroundColor: 'secondaryBackgroundDisabled',
            },
        },
        tertiary: {
            fontWeight: 'bold',
            borderRadius: 'none',
            backgroundColor: 'background',
            color: 'text',
            outlineColor: 'secondaryBackground',
            borderColor: 'backgroundMedium',
            $$hover: {
                color: 'tertiaryText',
                backgroundColor: 'secondaryBackgroundHover',
            },
            $$active: {
                color: 'tertiaryText',
                backgroundColor: 'secondaryBackgroundActive',
            },
            $$disabled: {
                color: 'secondaryText',
                backgroundColor: 'secondaryBackgroundDisabled',
            },
        },
        ghost: {
            fontWeight: 'bold',
            borderRadius: 'none',
            backgroundColor: 'background',
            color: 'text',
            outlineColor: 'secondaryBackground',
            borderColor: 'transparent',
            $$hover: {
                color: 'tertiaryText',
                backgroundColor: 'secondaryBackgroundHover',
            },
            $$active: {
                color: 'tertiaryText',
                backgroundColor: 'secondaryBackgroundActive',
            },
            $$disabled: {
                color: 'secondaryText',
                backgroundColor: 'background',
            },
        },
        slay: {
            fontWeight: 'bold',
            borderRadius: 'none',
            backgroundColor: 'errorBackground',
            color: 'background',
            outlineColor: 'errorBorder',
            borderColor: 'transparent',
            $$hover: {
                backgroundColor: 'errorBackgroundHover',
            },
            $$active: {
                color: 'text',
                backgroundColor: 'errorBackgroundActive',
            },
            $$disabled: {
                color: 'secondaryText',
                backgroundColor: 'secondaryBackgroundDisabled',
            },
        },
    },
    tag: {
        color: 'text',
        backgroundColor: 'backgroundWeak',
        paddingTop: '$1',
        paddingBottom: '$1',
        paddingLeft: '$2',
        paddingRight: '$2',
        $$hover: {
            color: 'tertiaryText',
            backgroundColor: 'secondaryBackgroundHover',
        },
        $$disabled: {
            color: 'secondaryBackgroundActive',
            backgroundColor: 'secondaryBackgroundDisabled',
        },
    },
    alerts: {
        warning: {
            backgroundColor: 'warningBackgroundWeak',
            color: 'warningIcon',
        },
        question: {
            backgroundColor: 'backgroundWeak',
            color: 'secondaryIcon',
        },
        error: {
            backgroundColor: 'errorBackgroundWeak',
            color: 'errorIcon',
        },
        success: {
            backgroundColor: 'successBackgroundWeak',
            color: 'successIcon',
        },
        info: {
            backgroundColor: 'infoBackgroundWeak',
            color: 'infoIcon',
        },
    },
    hotspot: {
        default: {
            backgroundColor: 'backgroundWeak',
        },
        active: {
            height: '$11',
            backgroundColor: 'tertiaryText',
        },
        error: {
            backgroundColor: 'errorBackgroundWeak',
        },
        disabled: {
            backgroundColor: 'secondaryBackgroundDisabled',
            color: 'secondaryText',
            height: '$8',
            cursor: 'not-allowed',
        },
    },
    tooltip: {
        color: 'secondaryBackground',
        backgroundColor: 'secondaryBackground',
        paddingTop: '$1',
        paddingBottom: '$1',
        paddingLeft: '$2',
        paddingRight: '$2',
        text: {
            color: 'tertiaryText',
        },
    },
    forms: {
        input: {
            borderColor: 'backgroundMedium',
            borderRadius: 'none',
            borderWidth: 1,
            paddingRight: '$10',
            paddingLeft: '$4',
            fontWeight: 'normal',
            fontSize: '$1',
            color: 'text',
        },
        select: {
            appearance: 'none',
            backgroundColor: 'background',
            borderColor: 'border',
            borderRadius: 'none',
            borderStyle: 'solid',
            borderWidth: 1,
            paddingLeft: '$4',
            paddingRight: '$9',
            fontSize: '$1',
            fontWeight: 'normal',
            color: 'text',
            height: '$8',
        },
        textarea: {
            borderColor: 'backgroundMedium',
            borderRadius: 'none',
            borderWidth: 1,
            paddingLeft: '$4',
            paddingRight: '$4',
            paddingTop: '$3',
            paddingBottom: '$3',
            fontWeight: 'normal',
            fontSize: '$1',
            color: 'text',
            lineHeight: '$3',
        },
        radio: {
            color: 'text',
            height: '$6',
            width: '$6',
            borderRadius: 'full',
            borderWidth: 1,
            borderColor: 'secondaryBorder',
            $$hover: {
                borderColor: 'transparent',
                backgroundColor: 'secondaryBackgroundDisabled',
            },
            $$disabled: {
                borderColor: 'secondaryBorderDisabled',
            },
        },
        checkbox: {
            borderWidth: 1,
            borderStyle: 'solid',
            borderColor: 'text',
            color: 'text',
            $$hover: {
                borderColor: 'transparent',
                backgroundColor: 'secondaryBackgroundDisabled',
            },
            $$checked: {
                color: 'tertiaryText',
                backgroundColor: 'secondaryBackground',
                borderColor: 'secondaryBackground',
            },
            $$focus: {
                borderColor: 'transparent',
                backgroundColor: 'secondaryBackgroundDisabled',
            },
            $$disabled: {
                borderWidth: 0,
                backgroundColor: 'secondaryBackgroundDisabled',
                color: 'secondaryBackgroundActive',
            },
        },
        switch: {
            $$off: {
                backgroundColor: 'secondaryBackgroundActive',
            },
            $$on: {
                backgroundColor: 'secondaryBackground',
            },
        },
    },
});
export { theme as base };
//# sourceMappingURL=base.js.map